/**
 * @fileoverview Main onboarding wizard page.
 * @module features/onboarding/pages/OnboardingPage
 */

import { ReactElement, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { OnboardingLayout } from '../components/OnboardingLayout'
import {
  useOnboardingState,
  VISIBLE_STEPS,
  clearOnboardingState,
} from '../hooks/useOnboardingState'
import { WelcomeStep } from '../components/steps/WelcomeStep'
import { CreateOrgStep } from '../components/steps/CreateOrgStep'
import { CurrencyStep } from '../components/steps/CurrencyStep'
import { SalesChannelStep } from '../components/steps/SalesChannelStep'
import { GoogleAdsStep } from '../components/steps/GoogleAdsStep'
import { FacebookStep } from '../components/steps/FacebookStep'
import { BankConnectionStep } from '../components/steps/BankConnectionStep'
import { CompletionStep } from '../components/steps/CompletionStep'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'

/**
 * Main onboarding wizard page.
 * Guides new users through workspace setup.
 * Create-org is a prerequisite step (no stepper), then 7 visible steps.
 *
 * @component
 */
export default function OnboardingPage(): ReactElement {
  const navigate = useNavigate()
  const { orgs, ready } = useActiveOrg()
  const redirectedRef = useRef(false)
  const {
    state,
    completeStep,
    skipStep,
    goToStep,
    setOrgData,
    setCurrency,
  } = useOnboardingState()

  // Redirect users who already have orgs and aren't mid-onboarding
  useEffect(() => {
    if (!ready || redirectedRef.current) return

    // User has orgs but onboarding state shows create-org step (fresh/stale state)
    // This means they shouldn't be here - redirect to home
    if (orgs.length > 0 && state.currentStep === 'create-org') {
      redirectedRef.current = true
      // Clear potentially stale onboarding state from testing other accounts
      clearOnboardingState()
      navigate('/', { replace: true })
    }
  }, [ready, orgs.length, state.currentStep, navigate])

  /** Handle step indicator click - convert visible index to step name */
  const handleStepClick = (visibleIndex: number): void => {
    const stepName = VISIBLE_STEPS[visibleIndex]
    if (stepName) {
      goToStep(stepName)
    }
  }

  /**
   * Render the current step component.
   */
  const renderStep = (): ReactElement => {
    switch (state.currentStep) {
      case 'create-org':
        return (
          <CreateOrgStep
            onComplete={(orgId, orgName) => {
              setOrgData(orgId, orgName)
              completeStep()
            }}
          />
        )

      case 'welcome':
        return <WelcomeStep onContinue={completeStep} />

      case 'currency':
        return (
          <CurrencyStep
            onComplete={(currency) => {
              setCurrency(currency)
              completeStep()
            }}
          />
        )

      case 'sales-channel':
        return (
          <SalesChannelStep
            onComplete={completeStep}
            onSkip={skipStep}
          />
        )

      case 'google-ads':
        return (
          <GoogleAdsStep
            onComplete={completeStep}
            onSkip={skipStep}
          />
        )

      case 'facebook':
        return (
          <FacebookStep
            onComplete={completeStep}
            onSkip={skipStep}
          />
        )

      case 'bank':
        return (
          <BankConnectionStep
            onComplete={completeStep}
            onSkip={skipStep}
          />
        )

      case 'completion':
        return <CompletionStep orgName={state.orgName} />

      default:
        return (
          <CreateOrgStep
            onComplete={(orgId, orgName) => {
              setOrgData(orgId, orgName)
              completeStep()
            }}
          />
        )
    }
  }

  return (
    <OnboardingLayout
      currentStep={state.visibleStepIndex}
      totalSteps={state.visibleTotalSteps}
      completedSteps={state.visibleCompletedIndices}
      showStepper={state.showStepper}
      onStepClick={handleStepClick}
    >
      {renderStep()}
    </OnboardingLayout>
  )
}
